import axios from 'utils/axios';
import { Order, OrderItem, Client, Product, PickingItem, OrderShipment, OrderStatus } from '../types/orders';
import { mockOrders } from '../utils/mocks/orders';
import { formatCPF, formatCNPJ } from './customers';

export type CreateOrderPayload = {
  /** Cliente: id do registro em customer_person ou customer_company (conforme clientKind). */
  clientId: string;
  /** Tipo do cliente: PERSON (customer_person) ou COMPANY (customer_company). */
  clientKind: 'PERSON' | 'COMPANY';
  /** UF do cliente (para a API: orders.state) */
  state: string;
  /** Nome da obra (orders.work_name) */
  workName?: string;
  /** Cor do pedido (orders.order_color) */
  orderColor?: string;
  /** Valor total da nota (orders.total_invoice_value). Se omitido, fica null na API. */
  totalInvoiceValue?: number;
  /** Soma dos valores dos produtos (orders.total_products_sum) */
  totalProductsSum?: number;
  /** Soma do ICMS dos itens (orders.total_icms_sum) */
  totalIcmsSum?: number;
  /** Soma do IPI dos itens (orders.total_ipi_sum) */
  totalIpiSum?: number;
  /** Soma do peso dos itens (orders.total_weight_sum) */
  totalWeightSum?: number;
  orderType: 'b2b' | 'b2c';
  paymentCondition: 'cash' | 'credit' | 'custom';
  /** Taxa de desconto customizada (fração, ex.: 0.38). Só usada quando paymentCondition = 'custom'. */
  customDiscountRate?: number;
  /** Desconto adicional (fração 0-1), à parte — ver taxCalculations.ts calculateNetPrice. */
  additionalDiscountRate?: number;
  /** Status do pedido (ex.: draft ao salvar rascunho, pending ao finalizar) */
  status?: Order['status'];
  items: Omit<OrderItem, 'id' | 'product'>[];
  freight?: number;
};

export type UpdateOrderPayload = Partial<CreateOrderPayload> & {
  status?: Order['status'];
};

export type OrdersListResponse = {
  message: string;
  data: Order[];
  pagination?: { page: number; limit: number; total: number };
};

export type ListOrdersParams = {
  search?: string;
  page?: number;
  limit?: number;
  status?: Order['status'];
  clientId?: string;
};

// --- Cliente retornado pela API (relação order.customer com person/company) ---
export type ApiOrderCustomer = {
  id: string;
  displayName: string;
  kind?: 'PERSON' | 'COMPANY';
  person?: { cpf: string };
  company?: { cnpj: string };
  omieClientId?: string | null;
  parentCustomer?: { id: string; displayName: string } | null;
};

// --- Formato retornado pela API (tabelas orders / order_items) ---
export type ApiOrder = {
  id: string;
  customerId: string | null;
  customer?: ApiOrderCustomer | null;
  workName: string | null;
  /** Observação do pedido (orders.notes) */
  notes?: string | null;
  state: string | null;
  orderColor: string | null;
  relationType: 'partner' | 'end_customer' | null;
  status?: Order['status'];
  // legado: avista/aprazo; atual: cash/credit/custom
  paymentCondition?: 'cash' | 'credit' | 'custom' | 'avista' | 'aprazo' | null;
  /** Desconto (fração 0-1) quando paymentCondition = 'custom'. */
  customDiscountRate?: number | null;
  /** Desconto adicional (fração 0-1), à parte. */
  additionalDiscountRate?: number | null;
  totalInvoiceValue: number | null;
  totalProductsSum: number | null;
  totalIcmsSum: number | null;
  totalIpiSum: number | null;
  totalWeightSum: number | null;
  items?: ApiOrderItem[];
  createdAt: string;
  updatedAt: string;
  invoicedAt?: string | null;
  omieFiscalScenarioCode?: string | null;
  omieOrderId?: string | null;
};

export type ApiOrderItem = {
  id: string;
  orderId: string;
  productId: string;
  lineNumber: number;
  quantity: number | null;
  roundedQuantity: number | null;
  fullNetPrice: number | null;
  totalProductsValue: number | null;
  icms: number | null;
  totalIpiValue: number | null;
  totalWeight: number | null;
  discounts: number | null;
  product?: ApiProduct;
};

export type ApiProduct = {
  id: string;
  preferenceCode: string | null;
  code: string | null;
  newCode: string | null;
  color: string | null;
  description: string | null;
  system: string | null;
  ncm: string | null;
  packagingUnit: number | null;
  unit: string | null;
  fraction: number | null;
  fullNetPrice: number | null;
  ipi: number | null;
  unitWeight: number | null;
  productGroup: number | null;
};

/** Garante número válido (API pode retornar decimal como string). */
function toNum(v: unknown): number {
  const n = Number(v);
  return Number.isFinite(n) ? n : 0;
}

/** Mapeia produto da API (catálogo ou item) para o tipo Product usado em pedidos no frontend */
export function mapApiProductToOrderProduct(p: ApiProduct): Product {
  return {
    id: p.id,
    codePreference: p.preferenceCode ?? '',
    codeOmie: p.code ?? p.newCode ?? '',
    color: p.color ?? '',
    description: p.description ?? '',
    system: p.system ?? '',
    ncm: p.ncm ?? '',
    packagingUnit: toNum(p.packagingUnit),
    unit: p.unit ?? 'm',
    fraction: toNum(p.fraction),
    tablePrice: toNum(p.fullNetPrice),
    ipiRate: toNum(p.ipi),
    icmsOrigin: 0,
    unitWeight: toNum(p.unitWeight),
    group: toNum(p.productGroup)
  };
}

function mapApiOrderItemToFrontend(item: ApiOrderItem): OrderItem {
  const product = item.product ? mapApiProductToOrderProduct(item.product) : ({} as Product);
  return {
    id: item.id,
    productId: item.productId,
    product: product,
    quantity: toNum(item.quantity),
    roundedQuantity: toNum(item.roundedQuantity),
    commercialDiscount: toNum(item.discounts),
    tablePrice: toNum(item.fullNetPrice ?? product?.tablePrice),
    netPrice: toNum(item.fullNetPrice),
    totalValue: toNum(item.totalProductsValue),
    ipiValue: toNum(item.totalIpiValue),
    weight: toNum(item.totalWeight)
  };
}

function mapApiOrderToFrontend(order: ApiOrder): Order {
  const normalizePaymentCondition = (v: any): 'cash' | 'credit' | 'custom' => {
    if (v === 'aprazo' || v === 'credit') return 'credit';
    if (v === 'custom') return 'custom';
    return 'cash';
  };
  const customer = order.customer;
  const document = customer?.person?.cpf
    ? formatCPF(customer.person.cpf)
    : customer?.company?.cnpj
      ? formatCNPJ(customer.company.cnpj)
      : '';
  const client: Client = {
    id: order.customerId ?? customer?.id ?? '',
    name: customer?.displayName ?? '',
    cnpj: document,
    uf: order.state ?? '',
    address: '',
    isConsumerFinal: order.relationType === 'end_customer',
    omieClientId: customer?.omieClientId ?? null,
    parentCustomer: customer?.parentCustomer ?? null
  };
  const items: OrderItem[] = (order.items ?? []).map(mapApiOrderItemToFrontend);
  const subtotal = Number(order.totalProductsSum ?? 0);
  const totalIpi = Number(order.totalIpiSum ?? 0);
  const totalIcms = Number(order.totalIcmsSum ?? 0);
  const grandTotal = Number(order.totalInvoiceValue ?? subtotal);
  const clientId = order.customer?.id ?? (order as any).customerId ?? customer?.id ?? '';
  return {
    id: order.id,
    number: order.id.slice(0, 8),
    clientId,
    client,
    workName: order.workName ?? undefined,
    notes: order.notes ?? undefined,
    state: order.state ?? undefined,
    orderColor: order.orderColor ?? undefined,
    orderType: order.relationType === 'end_customer' ? 'b2c' : 'b2b',
    paymentCondition: normalizePaymentCondition(order.paymentCondition),
    customDiscountRate: order.customDiscountRate ?? null,
    additionalDiscountRate: order.additionalDiscountRate ?? null,
    items,
    subtotalProducts: subtotal,
    totalIpi,
    totalIcms,
    totalDifal: 0,
    freight: 0,
    totalWeight: Number(order.totalWeightSum ?? 0),
    grandTotal,
    status: (order.status as any) ?? 'draft',
    createdAt: new Date(order.createdAt),
    updatedAt: new Date(order.updatedAt),
    invoicedAt: order.invoicedAt ? new Date(order.invoicedAt) : undefined,
    omieFiscalScenarioCode: order.omieFiscalScenarioCode ?? null,
    omieOrderId: order.omieOrderId ?? null
  };
}

export type OmieFiscalScenario = {
  code: string;
  name: string;
  isDefault: boolean;
};

/** Lista sempre atualizada de Cenários de Impostos ativos no Omie. */
export async function listOmieFiscalScenarios() {
  const { data } = await axios.get<{ data: OmieFiscalScenario[] }>(
    '/integrations/omie/orders/fiscal-scenarios'
  );
  return data.data;
}

/** Define o cenário que será enviado ao Omie quando este pedido for faturado. */
export async function setOrderOmieFiscalScenario(orderId: string, code: string | null) {
  const { data } = await axios.patch<{
    data: { orderId: string; omieFiscalScenarioCode: string | null };
  }>(`/integrations/omie/orders/${orderId}/fiscal-scenario`, { code });
  return data.data;
}

/**
 * List orders (API retorna { message, data: Order[] }; sem paginação no backend)
 */
export async function listOrders(params?: ListOrdersParams) {
  try {
    const { data } = await axios.get<{ message: string; data: ApiOrder[] }>('/orders');
    const orders = (data.data ?? []).map(mapApiOrderToFrontend);
    let filtered = orders;
    if (params?.search) {
      const q = params.search.toLowerCase();
      filtered = filtered.filter((o) => o.number.toLowerCase().includes(q) || o.client.name.toLowerCase().includes(q));
    }
    if (params?.status) {
      filtered = filtered.filter((o) => o.status === params.status);
    }
    if (params?.clientId) {
      filtered = filtered.filter((o) => o.clientId === params.clientId);
    }
    return {
      message: data.message ?? '',
      data: filtered,
      pagination: {
        page: params?.page ?? 1,
        limit: params?.limit ?? 20,
        total: filtered.length
      }
    };
  } catch (error: any) {
    console.warn('API não disponível, usando dados mockados:', error.message);
    let filteredOrders = [...mockOrders];
    if (params?.search) {
      const searchLower = params.search.toLowerCase();
      filteredOrders = filteredOrders.filter(
        (order) =>
          order.number.toLowerCase().includes(searchLower) ||
          order.client.name.toLowerCase().includes(searchLower) ||
          order.client.cnpj.toLowerCase().includes(searchLower)
      );
    }
    if (params?.status) {
      filteredOrders = filteredOrders.filter((order) => order.status === params.status);
    }
    return {
      message: 'Dados mockados',
      data: filteredOrders,
      pagination: {
        page: params?.page ?? 1,
        limit: params?.limit ?? 20,
        total: filteredOrders.length
      }
    };
  }
}

/**
 * Get order by ID (com itens e produto em cada item)
 */
export async function getOrder(id: string) {
  const { data } = await axios.get<{ message: string; data: ApiOrder }>(`/orders/${id}`);
  return mapApiOrderToFrontend(data.data);
}

export type OrderStatusHistoryEntry = {
  id: string;
  orderId: string;
  status: string;
  changedAt: string;
};

/** Timeline de status do pedido (ver OrderTimelineDialog). */
export async function getOrderStatusHistory(orderId: string) {
  const { data } = await axios.get<{ order: ApiOrder; history: OrderStatusHistoryEntry[] }>(
    `/orders/${orderId}/status-history`
  );
  return { order: mapApiOrderToFrontend(data.order), history: data.history };
}

/** Pedido mais recente do usuário logado — usado pela rota /orders/latest/timeline. */
export async function getLatestOrder() {
  const { data } = await axios.get<{ data: ApiOrder | null }>('/orders/latest');
  return data.data ? mapApiOrderToFrontend(data.data) : null;
}

/**
 * Cria pedido em uma única requisição (cabeçalho + itens no body).
 */
export async function createOrder(payload: CreateOrderPayload) {
  const relationType = payload.orderType === 'b2c' ? 'end_customer' : 'partner';
  const body = {
    customerPersonId: payload.clientKind === 'PERSON' ? payload.clientId : undefined,
    customerCompanyId: payload.clientKind === 'COMPANY' ? payload.clientId : undefined,
    state: payload.state,
    workName: payload.workName,
    orderColor: payload.orderColor,
    status: payload.status,
    paymentCondition: payload.paymentCondition,
    customDiscountRate: payload.customDiscountRate,
    additionalDiscountRate: payload.additionalDiscountRate,
    totalInvoiceValue: payload.totalInvoiceValue,
    totalProductsSum: payload.totalProductsSum,
    totalIcmsSum: payload.totalIcmsSum,
    totalIpiSum: payload.totalIpiSum,
    totalWeightSum: payload.totalWeightSum,
    relationType,
    items: payload.items.map((item) => ({
      productId: item.productId,
      quantity: item.quantity,
      roundedQuantity: item.roundedQuantity,
      discounts: item.commercialDiscount ?? 0,
      fullNetPrice: item.netPrice,
      totalProductsValue: item.totalValue,
      totalIpiValue: item.ipiValue,
      totalWeight: item.weight
    }))
  };
  const { data: orderData } = await axios.post<{ message: string; data: ApiOrder }>('/orders', body);
  const created = await getOrder(orderData.data.id);
  return created;
}

/**
 * Update order (cabeçalho: cliente, state, workName, orderColor, relationType, status, paymentCondition, totais).
 */
export async function updateOrder(id: string, payload: UpdateOrderPayload) {
  const body: Record<string, unknown> = {};
  if (payload.clientId !== undefined && payload.clientKind !== undefined) {
    body.customerPersonId = payload.clientKind === 'PERSON' ? payload.clientId : undefined;
    body.customerCompanyId = payload.clientKind === 'COMPANY' ? payload.clientId : undefined;
  }
  if (payload.state !== undefined) body.state = payload.state;
  if (payload.workName !== undefined) body.workName = payload.workName;
  if (payload.orderColor !== undefined) body.orderColor = payload.orderColor;
  if (payload.totalInvoiceValue !== undefined) body.totalInvoiceValue = payload.totalInvoiceValue;
  if (payload.totalProductsSum !== undefined) body.totalProductsSum = payload.totalProductsSum;
  if (payload.totalIcmsSum !== undefined) body.totalIcmsSum = payload.totalIcmsSum;
  if (payload.totalIpiSum !== undefined) body.totalIpiSum = payload.totalIpiSum;
  if (payload.totalWeightSum !== undefined) body.totalWeightSum = payload.totalWeightSum;
  if (payload.status !== undefined) body.status = payload.status;
  if (payload.paymentCondition !== undefined) body.paymentCondition = payload.paymentCondition;
  if (payload.customDiscountRate !== undefined) body.customDiscountRate = payload.customDiscountRate;
  if (payload.additionalDiscountRate !== undefined) body.additionalDiscountRate = payload.additionalDiscountRate;
  if (payload.orderType !== undefined) {
    body.relationType = payload.orderType === 'b2c' ? 'end_customer' : 'partner';
  }
  await axios.patch<{ message: string; data: ApiOrder }>(`/orders/${id}`, body);
  return getOrder(id);
}

export async function shipOrderItems(id: string, items: { itemId: string; quantity: number }[]) {
  const { data } = await axios.patch<{ message: string; data: ApiOrder }>(`/orders/${id}/ship-items`, { items });
  return data.data;
}

/** Salva a observação do pedido (texto vazio apaga). */
export async function saveOrderNotes(id: string, notes: string): Promise<string | null> {
  const { data } = await axios.patch<{ data: { notes: string | null } }>(`/orders/${id}/notes`, { notes });
  return data.data?.notes ?? null;
}

/** Nome fantasia do cliente PF no Omie ("EMPRESA - NOME"), para a impressão do pedido; null se não houver. */
export async function getOrderCustomerTradeName(id: string): Promise<string | null> {
  const { data } = await axios.get<{ data: { tradeName: string | null } }>(`/integrations/omie/orders/${id}/customer-trade-name`);
  return data.data?.tradeName ?? null;
}

/** Marca um pedido já separado (invoiced) como enviado; o cliente é avisado por e-mail. */
export async function markOrderShipped(id: string) {
  const { data } = await axios.patch<{ message: string; data: { id: string; status: string } }>(`/orders/${id}/mark-shipped`);
  return data.data;
}

export async function listShipments() {
  const { data } = await axios.get<{ message: string; data: any[] }>('/orders/shipments');
  return {
    ...data,
    data: data.data.map((s) => ({
      ...s,
      createdAt: new Date(s.createdAt),
      invoicedAt: new Date(s.invoicedAt),
      order: s.order ? mapApiOrderToFrontend(s.order) : undefined
    }))
  } as { message: string; data: OrderShipment[] };
}

export async function getShipment(id: string) {
  const { data } = await axios.get<{ message: string; data: any }>(`/orders/shipments/${id}`);
  const s = data.data;
  return {
    ...s,
    createdAt: new Date(s.createdAt),
    invoicedAt: new Date(s.invoicedAt),
    order: s.order ? mapApiOrderToFrontend(s.order) : undefined,
    items: (s.items || []).map((item: any) => ({
      ...item,
      orderItem: item.orderItem ? mapApiOrderItemToFrontend(item.orderItem) : undefined
    }))
  } as OrderShipment;
}

/**
 * Delete order
 */
export async function deleteOrder(id: string) {
  const { data } = await axios.delete<{ message: string }>(`/orders/${id}`);
  return data;
}

/** Payload para criar/atualizar item (productId, quantity, roundedQuantity, discounts, totais do front). */
export type OrderItemPayload = {
  productId: string;
  quantity?: number;
  roundedQuantity?: number;
  discounts?: number;
  fullNetPrice?: number;
  totalProductsValue?: number;
  totalIpiValue?: number;
  totalWeight?: number;
};

/**
 * Adiciona um item ao pedido (POST /orders/:orderId/items).
 */
export async function addOrderItem(orderId: string, item: OrderItemPayload) {
  const body: Record<string, unknown> = {
    productId: item.productId,
    quantity: item.quantity,
    roundedQuantity: item.roundedQuantity,
    discounts: item.discounts ?? 0
  };
  if (item.fullNetPrice !== undefined) body.fullNetPrice = item.fullNetPrice;
  if (item.totalProductsValue !== undefined) body.totalProductsValue = item.totalProductsValue;
  if (item.totalIpiValue !== undefined) body.totalIpiValue = item.totalIpiValue;
  if (item.totalWeight !== undefined) body.totalWeight = item.totalWeight;
  const { data } = await axios.post<{ message: string; data: ApiOrderItem }>(`/orders/${orderId}/items`, body);
  return data.data;
}

/**
 * Atualiza um item do pedido (PATCH /orders/:orderId/items/:itemId).
 */
export async function updateOrderItem(orderId: string, itemId: string, item: OrderItemPayload) {
  const body: Record<string, unknown> = {
    productId: item.productId,
    quantity: item.quantity,
    roundedQuantity: item.roundedQuantity,
    discounts: item.discounts
  };
  if (item.fullNetPrice !== undefined) body.fullNetPrice = item.fullNetPrice;
  if (item.totalProductsValue !== undefined) body.totalProductsValue = item.totalProductsValue;
  if (item.totalIpiValue !== undefined) body.totalIpiValue = item.totalIpiValue;
  if (item.totalWeight !== undefined) body.totalWeight = item.totalWeight;
  await axios.patch<{ message: string; data: ApiOrderItem }>(`/orders/${orderId}/items/${itemId}`, body);
  return getOrder(orderId);
}

/**
 * Remove um item do pedido (DELETE /orders/:orderId/items/:itemId).
 */
export async function deleteOrderItem(orderId: string, itemId: string) {
  await axios.delete<{ message: string }>(`/orders/${orderId}/items/${itemId}`);
  return getOrder(orderId);
}

/**
 * Get picking items for an order
 */
export async function getOrderPickingItems(orderId: string) {
  const { data } = await axios.get<{ message: string; data: PickingItem[] }>(`/orders/${orderId}/picking-items`);
  return data.data;
}

/**
 * Confirm picking item
 */
export async function confirmPickingItem(orderId: string, itemId: string) {
  const { data } = await axios.post<{ message: string; data: PickingItem }>(`/orders/${orderId}/picking-items/${itemId}/confirm`);
  return data.data;
}

/**
 * Descartar saldo pendente de um item de picking (remove da lista de separação).
 */
export async function dismissPickingItem(orderId: string, itemId: string) {
  const { data } = await axios.patch<{ message: string; data: any }>(`/orders/${orderId}/picking-items/${itemId}/dismiss`);
  return data;
}

/**
 * Search products for order
 */
export async function searchProducts(search?: string) {
  const { data } = await axios.get<{ message: string; data: Product[] }>('/products', {
    params: { search: search || undefined }
  });
  return data.data;
}

/**
 * Search clients for order
 */
export async function searchClients(search?: string) {
  const { data } = await axios.get<{ message: string; data: Client[] }>('/customers', {
    params: { search: search || undefined }
  });
  return data.data;
}

/**
 * Envia uma cópia da planilha importada (PrefGest ou Planilha Salamander) pro servidor,
 * só pra debug — o backend só grava de fato quando DEBUG_SAVE_IMPORTED_SHEETS=true no .env,
 * fora isso é um no-op. Fire-and-forget: chamador não deve travar a importação nem mostrar
 * erro ao usuário se isso falhar, é só um "melhor esforço" pra investigar depois.
 */
export async function debugUploadImportedSheet(file: File, context: string): Promise<void> {
  try {
    const form = new FormData();
    form.append('file', file);
    form.append('context', context);
    await axios.post('/orders/import-debug', form);
  } catch {
    // silencioso de propósito — nunca deve afetar o fluxo de importação do usuário
  }
}
